import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Factory, Loader2 } from "lucide-react";

export default function RegisterCompany() {
  const { registerCompany } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", owner_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await registerCompany(form);
      toast.success("Company registered");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-md bg-primary flex items-center justify-center corner-tick">
            <Factory className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading font-bold leading-none text-lg">Register Company</div>
            <div className="label-eyebrow">Create your workspace</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="company">Company name</Label>
            <Input id="company" value={form.company_name} onChange={set("company_name")} required
              placeholder="Acme Manufacturing Pvt Ltd" data-testid="register-company-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="owner">Your name (Owner)</Label>
            <Input id="owner" value={form.owner_name} onChange={set("owner_name")} required
              placeholder="Full name" data-testid="register-owner-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={set("email")} required
              placeholder="you@company.com" data-testid="register-email-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={form.password} onChange={set("password")} required
              placeholder="Choose a strong password" data-testid="register-password-input" className="mt-1.5" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create company
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:underline" data-testid="goto-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
